import React, { useState, useEffect } from 'react'
import { Head, Link } from '@inertiajs/inertia-react'
import { Inertia } from '@inertiajs/inertia'
import Layout from '../../Shared/Layout/LayoutAdmin'
import { Table, Button, Modal, Spinner } from 'react-bootstrap'
import Breadcrumbs from '../../Shared/Components/Breadcrumbs'
import LoadingOverlay from '../../Shared/Components/LoadingOverlay'
import { formatMoney } from '../../utils/formatters';

const Show = ({ metric, totalPolicies }) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedPolicyId, setSelectedPolicyId] = useState(null);
    const [note, setNote] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [showLoadingModal, setShowLoadingModal] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');

    useEffect(() => {
        // Reset loading state when component mounts or metric changes
        setIsLoading(false);
    }, [metric]);

    const monthNames = [
        'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];

    const handleDelete = (policyId) => {
        setSelectedPolicyId(policyId);
        setShowDeleteModal(true);
    };

    const confirmDelete = () => {
        if (note.trim().length < 10) {
            alert('El motivo debe tener al menos 10 caracteres');
            return;
        }

        setShowDeleteModal(false);
        setIsLoading(true);

        Inertia.delete(route('commission-metrics-policies.destroy', selectedPolicyId), {
            data: { note_cancelled: note },
            preserveScroll: true,
            onSuccess: () => {
                setNote('');
                setIsLoading(false);
                Inertia.reload();
            },
            onError: () => {
                setIsLoading(false);
            },
            onFinish: () => {
                setIsLoading(false);
            }
        });
    };

    // Función para descargar CFDI
    const handleDownloadCfdi = (metricId) => {
        setLoadingMessage('Descargando factura...');
        setShowLoadingModal(true);

        // Simular el proceso de descarga
        window.location.href = route('commission-metrics.download-cfdi', metricId);

        // Cerrar el modal después de 2 segundos (tiempo estimado para iniciar la descarga)
        setTimeout(() => {
            setShowLoadingModal(false);
        }, 2000);
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: 'badge badge-warning',
            paid: 'badge badge-success',
            cancelled: 'badge badge-danger'
        };
        return badges[status] || badges.pending;
    };

    const getStatusLabel = (status) => {
        const labels = {
            pending: 'Pendiente',
            paid: 'Pagado',
            cancelled: 'Cancelado'
        };
        return labels[status] || 'Pendiente';
    };

    return (
        <>
            <Head title="Detalle de Métrica" />

            <div className="mb-7 mt-7 d-flex justify-content-between align-items-center">
                <Breadcrumbs currentPath={`/admin/commission-metrics/${metric.id}`} />
                <Link
                    href={route('commission-metrics.adviser', metric.adviser_id)}
                    className="btn btn-light"
                >
                    <i className="fas fa-arrow-left me-2"></i>
                    Regresar a Comisiones del Asesor
                </Link>
            </div>

            {/* Modal de Loading para descarga */}
            <Modal
                show={showLoadingModal}
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Body className="text-center py-5">
                    <Spinner animation="border" variant="primary" className="mb-3" />
                    <h5 className="mb-2">{loadingMessage}</h5>
                    <p className="text-muted mb-0">Por favor espere mientras procesamos su solicitud</p>
                </Modal.Body>
            </Modal>

            <div className="card card-flush h-md-100">
                <div className="card-header">
                    <div>
                        <h3 className="card-title mb-2 mt-6">
                            Asesor: {metric.adviser?.user?.name}
                            {metric.adviser?.lastname_ap} {metric.adviser?.lastname_am}
                        </h3>
                        <div className="d-flex align-items-center gap-3">
                            {metric.invoice_commission_inbursa?.uuid ? (
                                <>
                                    <div className="text-muted">
                                        <i className="bi bi-receipt me-2"></i>
                                        <strong>UUID Factura:</strong>{' '}
                                        <span className="text-primary">{metric.invoice_commission_inbursa.uuid}</span>
                                    </div>
                                    <button
                                        className="btn btn-sm btn-success"
                                        onClick={() => handleDownloadCfdi(metric.id)}
                                    >
                                        <i className="bi bi-download me-1"></i>
                                        Descargar Factura
                                    </button>
                                </>
                            ) : (
                                <div className="text-muted fst-italic">
                                    <i className="bi bi-exclamation-circle me-2"></i>
                                    Sin factura generada
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    <div className="row mb-4">
                        <div className="col-md-3">
                            <strong>Período:</strong> {monthNames[metric.month - 1]} {metric.year}
                        </div>
                        <div className="col-md-3">
                            <strong>Total Prima:</strong> {formatMoney(metric.total_bonus)}
                        </div>
                        <div className="col-md-3">
                            <strong>Total Pólizas:</strong> {totalPolicies}
                        </div>
                        <div className="col-md-3">
                            <strong>Estado:</strong>{' '}
                            <span className={getStatusBadge(metric.status)}>
                                {getStatusLabel(metric.status)}
                            </span>
                        </div>
                    </div>

                    <Table striped bordered hover>
                        <thead>
                            <tr className="fs-7 fw-bold text-gray-400 border-bottom-0">
                                <th className="text-start">FOLIO</th>
                                <th className="text-start">PÓLIZA</th>
                                <th className="text-start">FOLIO INBURSA</th>
                                <th className="text-start"># PAGO</th>
                                <th className="text-start">% COMISIÓN</th>
                                <th className="text-start">ORIGEN %</th>
                                <th className="text-start">MONTO INBURSA</th>
                                <th className="text-start">MONTO COMISIÓN</th>
                                <th className="p-0 pb-3 w-50px text-end">ACCIONES</th>
                            </tr>
                        </thead>
                        <tbody>
                            {metric.commission_metrics_policies?.map(policy => (
                                <tr key={policy.id}>
                                    <td>{policy.folio}</td>
                                    <td>{policy.policy_relation?.policy}</td>
                                    <td>{policy.commission_inbursa?.folio}</td>
                                    <td>{policy.payment_number ?? 0}</td>
                                    <td>{Number(policy.commission_rate || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</td>
                                    <td>{policy.commission_rate_source}</td>
                                    <td>{formatMoney(policy.commission_inbursa_amount)}</td>
                                    <td>{formatMoney(policy.commission_amount)}</td>
                                    <td className="text-end">
                                        {policy.status !== 'paid' && (
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDelete(policy.id)}
                                                title="Eliminar póliza"
                                            >
                                                <i className="bi bi-trash"></i>
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* Modal de Confirmación de Eliminación */}
            <Modal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Confirmar Cancelación</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label required">
                            Motivo de la cancelación
                        </label>
                        <textarea
                            className="form-control"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            rows="3"
                            placeholder="Ingrese el motivo de la cancelación..."
                            required
                        />
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowDeleteModal(false)}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="danger"
                        onClick={confirmDelete}
                        disabled={!note.trim()}
                    >
                        Confirmar Cancelación
                    </Button>
                </Modal.Footer>
            </Modal>

            {isLoading && <LoadingOverlay />}
        </>
    )
}

Show.layout = page => <Layout children={page} title="Detalle de Métrica" />

export default Show
